import React, { useEffect, useState } from "react";
import { FaShieldAlt } from "react-icons/fa";
import { initAnalytics, disableAnalytics } from "../firebaseConfig";

const STORAGE_KEY = "analytics-consent"; // "granted" | "denied"

/**
 * Broadcast whether the consent bar is on screen so other floating UI
 * (the "Ask AI" button / chat window) can shift up and avoid overlapping it.
 * @param {boolean} open - Whether the bar is visible.
 */
function broadcastVisibility(open) {
  window.dispatchEvent(
      new CustomEvent("consent-banner-visibility", {detail: {open}}),
  );
}

/**
 * Analytics notice bar (opt-out model).
 *
 * Google Analytics (GA4) is enabled by default on load, which is permitted for
 * an Australian audience as long as visitors are informed and can opt out.
 * This slim full-width bottom bar discloses the use of analytics and lets the
 * visitor decline, which disables collection. The choice is stored in
 * localStorage so the bar does not reappear.
 */
const CookieConsent = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let choice = null;
    try {
      choice = localStorage.getItem(STORAGE_KEY);
    } catch {
      // localStorage unavailable (private mode) — treat as first visit.
    }

    if (choice === "denied") {
      // Respect a previous opt-out: keep analytics off.
      disableAnalytics();
    } else {
      // Default / previously accepted: analytics on.
      initAnalytics();
      if (choice !== "granted") {
        // First visit — show the disclosure bar.
        setVisible(true);
        broadcastVisibility(true);
      }
    }

    return () => broadcastVisibility(false);
  }, []);

  const dismiss = (value) => {
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch {
      // Ignore storage failures — choice simply won't be remembered.
    }
    if (value === "denied") {
      disableAnalytics();
    }
    setVisible(false);
    broadcastVisibility(false);
  };

  if (!visible) return null;

  return (
    <div
      role="region"
      aria-live="polite"
      aria-label="Analytics notice"
      className="fixed bottom-0 left-0 right-0 z-[9997] bg-slate-950/95 backdrop-blur text-slate-100 border-t border-slate-800 shadow-2xl"
    >
      <div className="max-w-6xl mx-auto px-4 py-3 md:py-3.5 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-start gap-3 flex-1">
          <FaShieldAlt
            className="text-blue-400 mt-0.5 shrink-0"
            aria-hidden="true"
          />
          <p className="text-xs md:text-sm text-slate-300 leading-relaxed">
            <span className="font-semibold text-slate-100">
              Privacy &amp; analytics.
            </span>{" "}
            This site uses Google Analytics to understand how visitors use it
            and to improve the experience. You can opt out at any time. No
            personal data is sold or shared.
          </p>
        </div>

        <div className="flex gap-2 justify-end shrink-0">
          <button
            type="button"
            onClick={() => dismiss("denied")}
            className="text-xs md:text-sm px-3 py-2 rounded-xl border border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white transition"
          >
            Opt out
          </button>
          <button
            type="button"
            onClick={() => dismiss("granted")}
            className="text-xs md:text-sm px-4 py-2 rounded-xl bg-blue-600 border border-blue-500 text-white font-semibold hover:bg-blue-700 transition whitespace-nowrap"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
